import { FC } from "react";
import { createPortal } from "react-dom";
import { Modal } from "../../../shared/ui";
import { isIos } from "../../../shared/lib/pwa";

type Props = {
  show: boolean;
  setShow: (show: boolean) => void;
};

/**
 * Manual install instructions, worded for the platform that is actually looking at them.
 *
 * iOS never fires `beforeinstallprompt`, so installing can only be explained — and Safari is
 * named explicitly because it is the only reliable path there. On Android the sheet exists
 * for a different reason: Chrome fires the event under heuristics of its own (and mutes it
 * for a long time after a dismissal), so when it stays quiet the ⋮ menu is the way in.
 *
 * Portaled to <body> because its host lives inside the burger drawer, whose `transform`
 * makes it the containing block for `position: fixed` — rendered in place, the "full-screen"
 * overlay would be trapped inside (and slide away with) the 288px drawer.
 */
export const InstallHelpSheet: FC<Props> = (props) =>
  createPortal(<InstallHelpSheetBody {...props} />, document.body);

const InstallHelpSheetBody: FC<Props> = ({ show, setShow }) => (
  <Modal show={show} setShow={setShow} title="Zum Home-Bildschirm hinzufügen">
    <div className="px-1 pb-2 text-base leading-relaxed text-gray-700">
      {isIos() ? (
        <>
          <p className="mb-4">
            Auf dem iPhone und iPad wird die App über Safari hinzugefügt:
          </p>
          <ol className="mb-5 list-decimal space-y-2 pl-5">
            <li>
              Diese Seite in <strong>Safari</strong> öffnen.
            </li>
            <li>Unten auf das Teilen-Symbol tippen.</li>
            <li>
              <strong>„Zum Home-Bildschirm“</strong> wählen und mit{" "}
              <strong>„Hinzufügen“</strong> bestätigen.
            </li>
          </ol>
        </>
      ) : (
        <>
          <p className="mb-4">So wird die App über das Chrome-Menü installiert:</p>
          <ol className="mb-5 list-decimal space-y-2 pl-5">
            <li>
              Oben rechts auf das Menü <strong>⋮</strong> tippen.
            </li>
            <li>
              <strong>„App installieren“</strong> wählen — je nach Version heißt der
              Eintrag <strong>„Zum Startbildschirm hinzufügen“</strong>.
            </li>
            <li>Mit „Installieren“ bestätigen.</li>
          </ol>
          <p className="mb-4 text-[14px] text-gray-500">
            Fehlt der Menüpunkt, unterstützt dieses Gerät das Installieren nicht —
            Benachrichtigungen funktionieren trotzdem, auch ohne Installation.
          </p>
        </>
      )}
      <p className="rounded-lg bg-[#FFF7E6] px-4 py-3 text-[15px] text-[#8A5A00]">
        Die App hat eine eigene Anmeldung: nach dem Hinzufügen melden Sie sich dort
        einmal neu an.
      </p>
    </div>
  </Modal>
);
